import { PropsWithChildren } from 'react';
import { LoanApplicationContext, LoanApplicationContextType } from './Context';
import { ApplicationStatusModel } from '@n2-common/serverGeneratedTypings/applicationStatusModel';
import { NavigateFunction } from 'react-router-dom';
import { getApplicatId, pollForExpectedApplicationState, postLoanApplication } from '@n2-common/services/legacyApplicationApiService';
import { N2ApplicationStatus } from '@n2-common/serverGeneratedTypings/n2ApplicationStatus';
import { PostLoanApplicationResult } from '@n2-common/models';
import { N2ApplicationStatusToECommerceStatus, trackCoreBankEvent, trackEcommerceEvent } from '@n2-common/lib/gtm';
import { ApplicationPaths, navigateToStep } from '@n2-common/applicationPages/ApplicationFormsHelpers';
import { navigateToCorrectStep } from '../../App';

type Result = PostLoanApplicationResult & { applicantId?: string; status?: ApplicationStatusModel };

const context: LoanApplicationContextType = {
    postLoanApplication: async function (cleanedValues): Promise<Result> {
        const [, res] = await postLoanApplication(cleanedValues, `/api/n2/norwegianloanapplication`);

        const status = await pollForExpectedApplicationState(
            res.ApplicationGuid,
            '',
            (res: ApplicationStatusModel) => res.Status === N2ApplicationStatus.Offer || res.Status === N2ApplicationStatus.Declined,
            false
        );

        if (!status) {
            return { applicationId: res.ApplicationGuid, outcome: 'Pending' };
        }

        const loanAmountForTracking = parseInt(cleanedValues.DesiredCredit?.replace(/\s/g, ''));

        trackEcommerceEvent(
            'CLA',
            'NO',
            'add_shipping_info',
            loanAmountForTracking,
            status.Gpid || 'not-found',
            res.ApplicationGuid,
            N2ApplicationStatusToECommerceStatus(status.MajorStatus)
        );
        trackCoreBankEvent('CLA', 'NO', 'application_status', status.Status === N2ApplicationStatus.Offer ? 'approved' : 'declined');

        const applicantIdModel = await getApplicatId(res.ApplicationGuid);
        if (!applicantIdModel.ApplicantId) {
            throw new Error('Error fetching applicantId after submitting');
        }

        return {
            applicationId: res.ApplicationGuid,
            outcome: status.Status == N2ApplicationStatus.Offer ? 'Approve' : 'Decline',
            applicantId: applicantIdModel.ApplicantId,
            status,
        };
    },

    redirect: function (result: Result, navigate: NavigateFunction) {
        if (result.outcome == 'Pending') {
            navigateToStep(ApplicationPaths.loadApplication, result.applicationId, navigate, result.applicantId);
            return;
        }
        if (!result.status || !result.applicantId) {
            throw new Error("Can't happen");
        }
        navigateToCorrectStep({ ...result.status, IsMainApplicant: true }, result.applicantId, navigate);
    },
};

export const LoanApplicationContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
    return <LoanApplicationContext.Provider value={context}>{children}</LoanApplicationContext.Provider>;
};
